import { Directive, Input, OnInit, OnDestroy, ElementRef, Renderer2, inject, output, forwardRef } from '@angular/core';
import { AbstractControl, FormControl, NG_VALIDATORS, ValidationErrors, Validator } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil, startWith } from 'rxjs/operators';

/**
 * Directive that adds a visual password strength meter to password input fields.
 * The meter displays a color-coded bar that indicates password strength based on various criteria:
 * - Minimum length (8 characters)
 * - Presence of lowercase letters
 * - Presence of uppercase letters
 * - Presence of numbers
 * - Presence of special characters
 */
@Directive({
  selector: '[libPasswordStrength]',
  standalone: true,
  providers: [
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => PasswordStrengthDirective),
      multi: true
    }
  ]
})
export class PasswordStrengthDirective implements OnInit, OnDestroy, Validator {
  /**
   * FormControl instance for the password input field
   */
  @Input('libPasswordStrength') passwordControl!: FormControl;
  public passwordScore = output<number>();

  private _minStrengthLevel = 0;

  @Input()
  set minStrengthLevel(value: number | string) {
    const level = typeof value === 'string' ? parseInt(value, 10) : value;
    this._minStrengthLevel = isNaN(level) ? 0 : Math.max(0, Math.min(4, level));
    if (this.onChange) {
      this.onChange();
    }
  }

  get minStrengthLevel(): number {
    return this._minStrengthLevel;
  }

  private readonly renderer = inject(Renderer2);
  private readonly el = inject(ElementRef);
  private readonly translateService = inject(TranslateService);
  private readonly destroy$ = new Subject<void>();
  private meterElement: HTMLElement | null = null;
  private labelElement: HTMLElement | null = null;
  private segments: HTMLElement[] = [];
  private onChange?: () => void;

  ngOnInit(): void {
    if (!this.passwordControl) {
      console.error('PasswordStrengthDirective: FormControl instance is required for [libPasswordStrength].');
    } else {
      this._createStrengthMeterElement();

      this.passwordControl.valueChanges
        .pipe(
          startWith(this.passwordControl.value),
          takeUntil(this.destroy$)
        )
        .subscribe((value: string | null) => {
          const strength = this._calculateStrength(value ?? '');
          this._updateMeterUI(strength);
          this.passwordScore.emit(strength);
        });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this._removeStrengthMeterElement();
  }

  /**
   * Creates the strength meter container and segments using Tailwind CSS classes
   * The meter consists of 4 segments that represent different strength levels
   * @private
   */
  private _createStrengthMeterElement(): void {
    const container = this.renderer.createElement('div');
    const containerClasses = ['strength-meter-container', 'flex', 'flex-col', 'gap-2', 'w-full'];
    containerClasses.forEach(cls => this.renderer.addClass(container, cls));

    this.meterElement = this.renderer.createElement('div');
    const meterClasses = ['strength-meter', 'flex', 'h-[4px]', 'w-full', 'dark:bg-muted-foreground', 'overflow-hidden', 'bg-muted-solid', 'rounded'];
    meterClasses.forEach(cls => this.renderer.addClass(this.meterElement, cls));

    this.labelElement = this.renderer.createElement('div');
    const labelClasses = ['content-sm-semibold'];
    labelClasses.forEach(cls => this.renderer.addClass(this.labelElement, cls));

    this.renderer.appendChild(container, this.meterElement);
    this.renderer.appendChild(container, this.labelElement);

    this.segments = [];
    for (let i = 0; i < 4; i++) {
      const segment = this.renderer.createElement('div');
      const segmentClasses = ['strength-meter-segment', 'flex-1', 'h-full', 'transition-colors', 'duration-300', 'ease-in-out'];
      segmentClasses.forEach(cls => this.renderer.addClass(segment, cls));
      this.renderer.appendChild(this.meterElement, segment);
      this.segments.push(segment);
    }

    const hostElement = this.el.nativeElement;
    const parentElement = this.renderer.parentNode(hostElement);
    const nextSibling = this.renderer.nextSibling(hostElement);

    if (parentElement) {
      if (nextSibling) {
        this.renderer.insertBefore(parentElement, container, nextSibling);
      } else {
        this.renderer.appendChild(parentElement, container);
      }
    }
  }

  /**
   * Removes the strength meter element from the DOM
   * @private
   */
  private _removeStrengthMeterElement(): void {
    const container = this.meterElement?.parentNode;
    if (container?.parentNode) {
      this.renderer.removeChild(container.parentNode, container);
    }
    this.meterElement = null;
    this.labelElement = null;
    this.segments = [];
  }

  /**
   * Calculates password strength based on common criteria
   * @param password - The password string to evaluate
   * @returns A number from 0 to 4 representing the strength level:
   * - 0: Empty
   * - 1: Very Weak (less than 8 characters) or Weak (8+ chars with 0-1 criteria)
   * - 2: Medium (8+ chars with 2 criteria)
   * - 3: Strong (8+ chars with 3 criteria)
   * - 4: Very Strong (8+ chars with 4 criteria)
   * @private
   */
  private _calculateStrength(password: string): number {
    let score = 0;
    if (!password) return 0;

    const hasLowercase = /[a-z]/.test(password);
    const hasUppercase = /[A-Z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasSpecial = /[!@#$%^&*()_+=[\]{};':"\\|,.<>/?-]+/.test(password);
    const hasMinLength = password.length >= 8;

    if (hasMinLength) score++;
    if (hasLowercase) score++;
    if (hasUppercase) score++;
    if (hasNumber) score++;
    if (hasSpecial) score++;

    if (password.length > 0 && password.length < 8) return 1;
    if (password.length === 0) return 0;

    if (score <= 2) return 1;
    if (score === 3) return 2;
    if (score === 4) return 3;
    if (score >= 5) return 4;

    return 0;
  }

  /**
   * Updates the UI of the strength meter based on the calculated strength level
   * Applies Tailwind CSS classes for colors and handles transitions
   * @param strengthLevel - The calculated strength level (0-4)
   * @private
   */
  private _updateMeterUI(strengthLevel: number): void {
    if (!this.meterElement || !this.labelElement || this.segments.length !== 4) return;

    const strengthLevelsData = [
      { label: '', bgColor: '', textColor: '' },
      { label: 'DIRECTIVES.PASSWORD_STRENGTH.VERY_WEAK', bgColor: 'bg-danger', textColor: 'text-danger' },
      { label: 'DIRECTIVES.PASSWORD_STRENGTH.WEAK', bgColor: 'bg-orange', textColor: 'text-orange' },
      { label: 'DIRECTIVES.PASSWORD_STRENGTH.STRONG', bgColor: 'bg-lime', textColor: 'text-lime' },
      { label: 'DIRECTIVES.PASSWORD_STRENGTH.VERY_STRONG', bgColor: 'bg-success', textColor: 'text-success' }
    ];

    const currentLevelData = strengthLevelsData[strengthLevel];
    const allTextColors = strengthLevelsData.map(level => level.textColor).filter(Boolean);
    const allBgColors = strengthLevelsData.map(level => level.bgColor).filter(Boolean);

    const translatedLabel = currentLevelData.label ? this.translateService.instant(currentLevelData.label) : '';
    this.renderer.setProperty(this.labelElement, 'textContent', translatedLabel);
    allTextColors.forEach(cls => this.renderer.removeClass(this.labelElement, cls));
    if (currentLevelData.textColor) {
      this.renderer.addClass(this.labelElement, currentLevelData.textColor);
    }

    this.segments.forEach((segment, index) => {
      allBgColors.forEach(cls => this.renderer.removeClass(segment, cls));
      this.renderer.removeClass(segment, 'rounded-r-full');

      if (index < strengthLevel) {
        if (currentLevelData.bgColor) {
          this.renderer.addClass(segment, currentLevelData.bgColor);
        }
        if (index === strengthLevel - 1) {
          this.renderer.addClass(segment, 'rounded-r-full');
        }
      }
    });
  }

  validate(control: AbstractControl): ValidationErrors | null {
    const password = control.value;
    const strength = this._calculateStrength(password ?? '');

    if (strength < this._minStrengthLevel) {
      return {
        passwordStrength: {
          requiredStrength: this._minStrengthLevel,
          actualStrength: strength
        }
      };
    }
    return null;
  }

  registerOnValidatorChange(fn: () => void): void {
    this.onChange = fn;
  }
}
